import { FC, ReactNode, useMemo } from 'react'
import cn from 'classnames'
import styles from 'components/features/pfm/FloatingPaneHeader/FloatingPaneHeader.styl'
import themes from 'styles/base/themes.styl'
import { Theme } from 'interfaces/component'
import SvgIcon from 'components/atoms/SvgIcon'
import CrossSvg from 'icons/cross.svg'
import { IconCircled } from 'components/features/pfm/IconCircled/IconCircled'
import Button from 'components/features/pfm/Button/Button'
import { getSvgByFilename } from 'helpers/mediaHelpers'
import ProgressBar from 'components/atoms/ProgressBar/ProgressBar'

export type FloatingPaneHeaderProps = {
  title: ReactNode
  superTitle?: string
  subTitle?: ReactNode
  theme?: Theme
  iconSlug?: string
  withCircledIcon?: boolean
  progressBadge?: ReactNode
  progressBarPercent?: number
  isLoading?: boolean
  centered?: boolean
  compact?: boolean
  onClose?: () => void
  onAction?: () => void
  actionText?: string
}

export const FloatingPaneHeader: FC<FloatingPaneHeaderProps> = (props) => {
  const {
    progressBadge,
    progressBarPercent = null,
    title,
    superTitle,
    subTitle,
    iconSlug,
    withCircledIcon,
    isLoading,
    centered,
    compact,
    theme = 'blue',
    actionText = 'Back',
    onAction,
    onClose,
  } = props

  const actionButtonChild = useMemo(() => !!onAction && (
    <Button
      style='textOnly'
      text={ actionText }
      size='tiny'
      onClick={ onAction }
      className={ styles.headerAction }
    />
  ), [onAction, actionText])

  const textClasses = cn(themes.textOnly, themes[theme])

  const superTitleChild = useMemo(() => !!superTitle && (
    <small className={ cn(styles.superTitle, textClasses) }>{ superTitle }</small>
  ), [superTitle, textClasses])

  const titleChild = useMemo(() => !!title && (
    <h2 className={ styles.title }>{ title }</h2>
  ), [title])

  const subTitleChild = useMemo(() => !!subTitle && (
    <h3 className={ cn(styles.subTitle, textClasses) }>{ subTitle }</h3>
  ), [subTitle, textClasses])

  const iconChild = useMemo(() => {
    if (!iconSlug) {
      return null
    }

    const icon = withCircledIcon ? (
      <IconCircled iconSlug={ iconSlug } theme={ theme } />
    ) : (
      <i><SvgIcon src={ getSvgByFilename(iconSlug) } /></i>
    )

    return (
      <div className={ styles.iconWrapper }>
        { icon }
      </div>
    )
  }, [iconSlug, theme, withCircledIcon])

  const badgeChild = useMemo(() => !!progressBadge && (
    <div className={ styles.progressBadge }>
      <div className={ styles.badgeContent }>
        <div className={ styles.badge } />
        { progressBadge }
      </div>
    </div>
  ), [progressBadge])

  const classNames = cn(
    styles.floatingPaneHeader, {
      [styles.centered]: centered,
      [styles.compact]: compact,
      [styles.withIcon]: !!iconSlug,
    },
  )

  const closeButtonChild = useMemo(() => {
    return onClose && (
      <button className={ styles.cross } onClick={ onClose } type='button'>
        <SvgIcon src={ CrossSvg } />
      </button>
    )
  }, [onClose])

  return (
    <div className={ classNames }>
      { closeButtonChild }
      <ProgressBar progressPercent={ progressBarPercent } isLoading={ isLoading } theme={ theme } pinToTop />
      { actionButtonChild }
      { superTitleChild }
      { badgeChild || iconChild }
      { titleChild }
      { subTitleChild }
    </div>
  )
}
